import { getAttributeValue } from '@theme/dom';
import { debounce, isFromBreakpoint } from '@theme/helpers';
import { HTMLCustomElement } from '@theme/html-custom-element';

/**
 * <marquee-element> custom element
 * Attributes:
 * - speed: pixels per second (default 50)
 * - gap: pixels between slides (default 16)
 */
export class MarqueeElement extends HTMLCustomElement {
  constructor() {
    super();

    // Create track container in light DOM
    this.track = document.createElement('div');
    this.track.className = 'track';

    this.resizeObserver = new ResizeObserver(
      debounce(() => {
        const currentWidth = this.offsetWidth;

        if (currentWidth !== this.previousWidth) {
          this.previousWidth = currentWidth;
          this.setup();
        }
      }, 50),
    );
  }

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    // Append track container if not already appended
    if (!this.contains(this.track)) {
      this.appendChild(this.track);
    }

    // Move original slides into track container
    const originalSlides = Array.from(this.children).filter((child) => child !== this.track);
    originalSlides.forEach((slide) => this.track.appendChild(slide));

    // Now cache the slides inside track (original slides only)
    this.slides = Array.from(this.track.children).map((child) => {
      const clone = child.cloneNode(true);
      clone.classList.add('slide');
      return clone;
    });

    this.setup();

    this.resizeObserver.observe(this);

    this.on('mouseenter', this, () => {
      if (window.matchMedia('(hover: hover)').matches) {
        this.track.style.animationPlayState = 'paused';
      }
    });

    this.on('touchstart', this, () => {
      this.track.style.animationPlayState = 'paused';
    });

    this.on('mouseleave', this, () => {
      this.track.style.animationPlayState = 'running';
    });

    this.on('touchend', this, () => {
      this.track.style.animationPlayState = 'running';
    });
  }

  disconnectedCallback() {
    this.resizeObserver.disconnect();
  }

  /**
   * The speed of the marquee.
   * @returns {Number}
   */
  get speed() {
    return getAttributeValue(this, 'speed', 50);
  }

  /**
   * The gap between each item.
   * @returns {Number}
   */
  get gap() {
    const gap = getAttributeValue(this, 'gap', 16);

    if (isFromBreakpoint('large')) {
      return getAttributeValue(this, 'gap-large', gap);
    }

    return gap;
  }

  /**
   * Builds the clones and starts the animation.
   */
  setup() {
    this.track.innerHTML = '';

    [...this.slides, ...this.slides].forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.style.marginRight = `${this.gap}px`;
      this.track.appendChild(clone);
    });

    const containerWidth = this.offsetWidth;
    let trackWidth = this.track.scrollWidth;
    let count = 0;

    while (trackWidth < containerWidth * 2 && count < 50) {
      this.slides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.style.marginRight = `${this.gap}px`;
        this.track.appendChild(clone);
      });
      trackWidth = this.track.scrollWidth;
      count++;
    }

    const duration = trackWidth / this.speed;

    this.track.style.animation = `marquee-scroll ${duration}s linear infinite`;
  }
}

if (!customElements.get('marquee-element')) customElements.define('marquee-element', MarqueeElement);
